import React, { useState, useEffect } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import { FAQService } from '../../services/faqService';
import { FAQModel } from '../../types/faq';
import AddFAQDialog from './AddFAQDialog';
import EditFAQDialog from './EditFAQDialog';
import DeleteFAQDialog from './DeleteFAQDialog';

import { Plus, Edit, Trash2, GripVertical } from 'lucide-react';

import { toast } from 'react-toastify';

const FAQ = () => {
  const [faqs, setFaqs] = useState<FAQModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState<FAQModel | null>(null);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [selectedFAQ, setSelectedFAQ] = useState<FAQModel | null>(null);

  useEffect(() => {
    loadFAQs();
  }, []);

  const loadFAQs = async () => {
    try {
      setIsLoading(true);
      const faqsData = await FAQService.loadFAQs();
      setFaqs(faqsData);
    } catch (error) {
      console.error('Error loading FAQs:', error);
      toast.error('Failed to load FAQs. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddFAQ = () => {
    setShowAddDialog(true);
  };

  const handleEditFAQ = (faq: FAQModel) => {
    setSelectedFAQ(faq);
    setShowEditDialog(true);
  };

  const handleDeleteFAQ = (faq: FAQModel) => {
    setSelectedFAQ(faq);
    setShowDeleteDialog(true);
  };

  const handleSaveFAQ = async (faq: FAQModel) => {
    try {
      const nextOrder = await FAQService.getNextOrder();
      await FAQService.createFAQ({ ...faq, order: nextOrder });
      await loadFAQs();
      toast.success('FAQ added successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Failed to add FAQ. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleUpdateFAQ = async (updatedFAQ: FAQModel) => {
    try {
      await FAQService.updateFAQ(updatedFAQ);
      await loadFAQs();
      toast.success('FAQ updated successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error updating FAQ:', error);
      toast.error('Failed to update FAQ. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      throw error;
    }
  };

  const handleDeleteFAQConfirm = async () => {
    if (selectedFAQ) {
      try {
        await FAQService.deleteFAQ(selectedFAQ.id);
        await loadFAQs();
        toast.success('FAQ deleted successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } catch (error) {
        console.error('Error deleting FAQ:', error);
        toast.error('Failed to delete FAQ. Please try again.', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        throw error;
      }
    }
  };

  const handleDragStart = (e: React.DragEvent, faq: FAQModel) => {
    setDraggedItem(faq);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetFAQ: FAQModel) => {
    e.preventDefault();

    if (!draggedItem || draggedItem.id === targetFAQ.id) {
      setDraggedItem(null);
      return;
    }

    const draggedIndex = faqs.findIndex(faq => faq.id === draggedItem.id);
    const targetIndex = faqs.findIndex(faq => faq.id === targetFAQ.id);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null);
      return;
    }

    const newFAQs = [...faqs];
    const [removed] = newFAQs.splice(draggedIndex, 1);
    newFAQs.splice(targetIndex, 0, removed);

    setFaqs(newFAQs);

    try {
      await FAQService.updateFAQOrder(newFAQs);
      toast.success('FAQ order updated successfully!', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error('Error updating FAQ order:', error);
      await loadFAQs();
      toast.error('Failed to update FAQ order. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }

    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleCloseAddDialog = () => setShowAddDialog(false);
  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setSelectedFAQ(null);
  };
  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setSelectedFAQ(null);
  };

  return (
    <React.Fragment>
      {/* Desktop Breadcrumb */}
      <div className="hidden md:block mb-5">
        <BreadCrumb title='FAQs' pageTitle='Dashboard' />
        <div className="flex justify-end mt-3">
          <button
            onClick={handleAddFAQ}
            className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
          >
            <Plus className="inline-block size-4 mr-1" /> <span className="align-middle">Add New FAQ</span>
          </button>
        </div>
      </div>

      {/* Mobile Header with Breadcrumb and Title */}
      <div className="flex flex-row items-center justify-between gap-4 py-4 md:hidden mb-5">
        <h6 className="text-15 font-semibold">FAQs</h6>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 dark:text-zink-400">Dashboard</span>
          <span className="text-slate-400 dark:text-zink-500">›</span>
          <span className="text-sm text-slate-500 dark:text-zink-400">FAQs</span>
        </div>
      </div>

      {/* Mobile Add Button */}
      <div className="flex justify-end mb-5 md:hidden">
        <button
          onClick={handleAddFAQ}
          className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
        >
          <Plus className="inline-block size-4 mr-1" /> <span className="align-middle">Add New FAQ</span>
        </button>
      </div>
      <div className="col-span-12">
        <div className="max-w-[70%] mx-auto">

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : faqs.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-slate-500 dark:text-zink-200">No FAQs found</p>
          </div>
        ) : (
          <div className="space-y-4 mb-20">
            {faqs.map((faq, index) => (
              <div
                key={faq.id}
                draggable
                onDragStart={(e) => handleDragStart(e, faq)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, faq)}
                onDragEnd={handleDragEnd}
                className={`bg-white dark:bg-zink-700 border border-slate-200 dark:border-zink-500 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-move ${
                  draggedItem?.id === faq.id ? 'opacity-50' : ''
                } p-3 md:p-4 mx-1 md:mx-0`}
              >
                {/* Mobile View - Questions Only */}
                <div className="block md:hidden">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 flex items-center justify-center h-full">
                      <GripVertical className="size-4 text-slate-400 dark:text-zink-500 cursor-grab active:cursor-grabbing" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h6 className="text-sm font-semibold text-slate-800 dark:text-zink-100 break-words">
                        {faq.question}
                      </h6>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleEditFAQ(faq)}
                        className="flex items-center justify-center size-7 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-blue-500 dark:hover:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-500/20"
                        title="Edit FAQ"
                      >
                        <Edit className="size-3" />
                      </button>
                      <button
                        onClick={() => handleDeleteFAQ(faq)}
                        className="flex items-center justify-center size-7 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-red-500 dark:hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20"
                        title="Delete FAQ"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Desktop View - Questions and Answers */}
                <div className="hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 flex items-center justify-center h-full">
                      <GripVertical className="size-4 text-slate-400 dark:text-zink-500 cursor-grab active:cursor-grabbing" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h6 className="text-sm font-semibold text-slate-800 dark:text-zink-100 mb-1 break-words">
                        {faq.question}
                      </h6>
                      <p className="text-xs text-slate-600 dark:text-zink-300 break-words">
                        {faq.answer}
                      </p>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleEditFAQ(faq)}
                        className="flex items-center justify-center size-7 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-blue-500 dark:hover:text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-500/20"
                        title="Edit FAQ"
                      >
                        <Edit className="size-3" />
                      </button>
                      <button
                        onClick={() => handleDeleteFAQ(faq)}
                        className="flex items-center justify-center size-7 transition-all duration-200 ease-linear rounded-md bg-slate-100 dark:bg-zink-600 dark:text-zink-200 text-slate-500 hover:text-red-500 dark:hover:text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20"
                        title="Delete FAQ"
                      >
                        <Trash2 className="size-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      <AddFAQDialog
        onClose={handleCloseAddDialog}
        onSave={handleSaveFAQ}
        show={showAddDialog}
      />

      {selectedFAQ && (
        <EditFAQDialog
          faq={selectedFAQ}
          onClose={handleCloseEditDialog}
          onSave={handleUpdateFAQ}
          show={showEditDialog}
        />
      )}

      {selectedFAQ && (
        <DeleteFAQDialog
          faq={selectedFAQ}
          onClose={handleCloseDeleteDialog}
          onDelete={handleDeleteFAQConfirm}
          show={showDeleteDialog}
        />
      )}
    </React.Fragment>
  );
};

export default FAQ;